import { useState } from 'react';
import { Box, Button, Chip, IconButton, Popover, Stack, TextField, Tooltip, Typography } from '@mui/material';
import PaletteOutlinedIcon from '@mui/icons-material/PaletteOutlined';
import { GROUP_COLOR_PRESETS, normalizeHexColor } from '../posterColorUtils';

/**
 * Small popover-based color picker for a tournament group.
 * Shows a swatch (current color or palette icon if none). Click opens palette + hex field + clear.
 *
 * Props:
 *   - value: current "#rrggbb" or empty/null
 *   - onChange(newColor | null): called when user picks a color or clears it
 *   - disabled: disable the trigger
 *   - label: aria/title label, e.g. "Color del Grupo A"
 */
export default function GroupColorPicker({ value, onChange, disabled, label = 'Color del grupo' }) {
  const [anchor, setAnchor] = useState(null);
  const [draft, setDraft] = useState(value || '');
  const open = Boolean(anchor);
  const current = value || '';

  const openPopover = (e) => {
    setDraft(current);
    setAnchor(e.currentTarget);
  };
  const closePopover = () => setAnchor(null);

  const applyColor = (color) => {
    const normalized = color ? normalizeHexColor(color) : '';
    onChange(normalized || null);
    closePopover();
  };

  return (
    <>
      <Tooltip title={label}>
        <Chip
          size="small"
          icon={<PaletteOutlinedIcon sx={{ color: current ? '#fff' : '#0f3531' }} />}
          label={current ? 'Color' : 'Sin color'}
          onClick={disabled ? undefined : openPopover}
          disabled={disabled}
          aria-label={label}
          sx={{
            cursor: disabled ? 'not-allowed' : 'pointer',
            fontWeight: 700,
            bgcolor: current || 'rgba(15,53,49,0.06)',
            color: current ? '#0a0a0a' : '#0f3531',
            border: current ? `1px solid ${current}` : '1px solid rgba(15,53,49,0.25)',
            '& .MuiChip-icon': { color: current ? '#0a0a0a' : '#0f3531' },
            '&:hover': {
              bgcolor: current ? current : 'rgba(15,53,49,0.12)',
              filter: current ? 'brightness(0.95)' : undefined,
            },
          }}
        />
      </Tooltip>

      <Popover
        open={open}
        anchorEl={anchor}
        onClose={closePopover}
        anchorOrigin={{ vertical: 'bottom', horizontal: 'right' }}
        transformOrigin={{ vertical: 'top', horizontal: 'right' }}
        PaperProps={{ sx: { p: 1.5, width: 240 } }}
      >
        <Typography variant="caption" sx={{ fontWeight: 700, color: 'text.secondary', display: 'block', mb: 1 }}>
          Color del grupo
        </Typography>
        <Box
          sx={{
            display: 'grid',
            gridTemplateColumns: 'repeat(5, 1fr)',
            gap: 0.75,
            mb: 1.5,
          }}
        >
          {GROUP_COLOR_PRESETS.map((c) => (
            <IconButton
              key={c}
              size="small"
              onClick={() => applyColor(c)}
              aria-label={`Usar ${c}`}
              sx={{
                width: 32,
                height: 32,
                borderRadius: '50%',
                bgcolor: c,
                border: c.toLowerCase() === current.toLowerCase() ? '3px solid #0f3531' : '1px solid rgba(0,0,0,0.12)',
                '&:hover': { bgcolor: c, opacity: 0.85 },
              }}
            />
          ))}
        </Box>
        <Stack direction="row" spacing={1} alignItems="center" sx={{ mb: 1.5 }}>
          <TextField
            size="small"
            label="Hex"
            placeholder="#7ed957"
            value={draft}
            onChange={(e) => setDraft(e.target.value)}
            inputProps={{ maxLength: 7 }}
            sx={{ flex: 1 }}
          />
          <Button
            size="small"
            variant="contained"
            onClick={() => applyColor(draft)}
            disabled={!normalizeHexColor(draft)}
            sx={{ textTransform: 'none', bgcolor: '#0f6b5c', '&:hover': { bgcolor: '#0c5a4e' } }}
          >
            OK
          </Button>
        </Stack>
        <Button
          size="small"
          fullWidth
          variant="outlined"
          onClick={() => applyColor('')}
          sx={{ textTransform: 'none', borderColor: 'rgba(15,53,49,0.3)', color: '#0f3531' }}
        >
          Quitar color
        </Button>
      </Popover>
    </>
  );
}
